import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import api, { formatApiError, createInvite, adminResetPassword } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { roleOptionsFor, GLOBAL_ROLES, CLIENT_ROLES } from "../lib/utils";
import UserForm from "../components/UserForm";

export default function UserFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const roleOptions = useMemo(() => roleOptionsFor(user.role), [user.role]);
  const [orgs, setOrgs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    name: "", email: "",
    role: roleOptions[0] || "client_user", is_active: true, assignments: [],
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const projectsByOrg = useMemo(() => {
    const m = {};
    for (const p of projects) (m[p.organization_id] = m[p.organization_id] || []).push(p);
    return m;
  }, [projects]);

  useEffect(() => {
    api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/users/${id}`).then((r) => {
      const u = r.data.user || r.data;
      setForm({
        name: u.name || "", email: u.email || "",
        role: u.role, is_active: u.is_active !== false, assignments: u.assignments || [],
      });
    }).catch((err) => setError(formatApiError(err.response?.data?.detail) || "Could not load user"));
  }, [id, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!GLOBAL_ROLES.includes(form.role)) {
      // client roles belong to exactly one client; team members may start unassigned
      if (CLIENT_ROLES.includes(form.role)) {
        if (form.assignments.length === 0) {
          setError("Assign the client this user belongs to.");
          return;
        }
        if (form.assignments.length > 1) {
          setError("Client roles can belong to only one client — remove the extra ones.");
          return;
        }
      }
      const bad = form.assignments.find((a) => !a.all_projects && (a.project_ids || []).length === 0);
      if (bad) {
        const org = orgs.find((o) => o.id === bad.organization_id);
        setError(`Select at least one project for ${org ? org.name : "this client"} (or turn on All projects).`);
        return;
      }
    }
    setSaving(true);
    const assignments = GLOBAL_ROLES.includes(form.role) ? [] : form.assignments;
    try {
      if (isEdit) {
        await api.patch(`/users/${id}`, { name: form.name, role: form.role, is_active: form.is_active, assignments });
      } else {
        await createInvite({
          email: form.email, role: form.role, assignments,
        });
      }
      navigate("/users");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="user-form-page">
      <button onClick={() => navigate("/users")} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 mb-3">
        <ArrowLeft size={14} /> Back to users
      </button>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">Access management</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">{isEdit ? "Edit user" : "Add user"}</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isEdit ? "Update this person's role, access, and status." : "Send an email invitation — they'll set their own password. Choose their role and access below."}
        </p>
      </div>
      <UserForm
        mode={isEdit ? "edit" : "create"}
        value={form}
        onChange={setForm}
        roleOptions={roleOptions}
        orgs={orgs}
        projectsByOrg={projectsByOrg}
        onSubmit={submit}
        submitting={saving}
        error={error}
        onCancel={() => navigate("/users")}
        userId={id}
        onResetPassword={adminResetPassword}
      />
    </div>
  );
}
